import { Minus, Plus, AlertCircle, Bell, Clock, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn, formatBRLOrFree } from "@/lib/utils";
import { calculateBuyerServiceFee, calculateTicketTotal } from "@/lib/ticket-pricing";
import { useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { joinWaitlist } from "@/lib/api";
import { toast } from "@/hooks/use-toast";
import { formatEventDateTimeRange, resolveEventTimeZone } from "@/lib/event-timezone";
import { useTranslation } from "react-i18next";

interface TicketTierCardProps {
  id: string;
  name: string;
  description?: string | null;
  price: number;
  originalPrice?: number | null;
  quantityTotal: number;
  quantitySold: number;
  quantityReserved?: number;
  minPerOrder: number;
  maxPerOrder: number;
  tierType: string;
  salesStartDate?: string | null;
  salesEndDate?: string | null;
  timeZone?: string | null;
  eventId?: string;
  onAdd: (tierId: string, quantity: number) => void;
  className?: string;
  includesCertificate?: boolean;
  sectorColor?: string;
  platformFeePercent?: number | null;
  absorbFee?: boolean | null;
}

export function TicketTierCard({
  id, name, description, price, originalPrice, quantityTotal, quantitySold,
  quantityReserved = 0, minPerOrder, maxPerOrder, tierType,
  salesStartDate, salesEndDate, timeZone, eventId, onAdd, className,
  includesCertificate, sectorColor, platformFeePercent, absorbFee,
}: TicketTierCardProps) {
  const { t } = useTranslation("eventDetail");
  const [quantity, setQuantity] = useState(0);
  const [joiningWaitlist, setJoiningWaitlist] = useState(false);
  // A10: Double-click protection
  const [isAdding, setIsAdding] = useState(false);
  const { user } = useAuth();
  const available = quantityTotal - quantitySold - quantityReserved;
  const isSoldOut = available <= 0;
  // "Últimas unidades" só faz sentido se já houve venda: um lote pequeno (<= 20)
  // com 0 vendidos não é escassez. Corte percentual cobre lotes pequenos.
  const isLow =
    available > 0 &&
    quantitySold > 0 &&
    (available <= 20 || available / quantityTotal <= 0.2);

  const now = new Date();
  const salesStart = salesStartDate ? new Date(salesStartDate) : null;
  const salesEnd = salesEndDate ? new Date(salesEndDate) : null;
  const resolvedTimeZone = resolveEventTimeZone(timeZone);
  const isNotYetOnSale = salesStart && salesStart > now;
  const isSalesEnded = salesEnd && salesEnd < now;

  const increment = () => setQuantity((q) => Math.min(q + 1, Math.min(maxPerOrder, available)));
  const decrement = () => setQuantity((q) => Math.max(q - 1, 0));

  const formatCountdown = (date: Date) => {
    const diff = date.getTime() - now.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}min`;
    return `${minutes}min`;
  };

  // A10: Debounced add with protection
  const handleAdd = useCallback(() => {
    if (isAdding) return;
    
    if (quantity < minPerOrder) {
      toast({ 
        title: t("ticketTier.toast.minQuantityTitle"),
        description: t("ticketTier.toast.minQuantityDesc", { min: minPerOrder }),
        variant: "destructive" 
      });
      return;
    }
    
    setIsAdding(true);
    onAdd(id, quantity);
    setTimeout(() => setIsAdding(false), 300);
  }, [isAdding, id, quantity, onAdd, minPerOrder, t]);

  const handleJoinWaitlist = async () => {
    if (!user || !eventId) {
      toast({ title: t("ticketTier.toast.loginWaitlist"), variant: "destructive" });
      return;
    }
    setJoiningWaitlist(true);
    try {
      await joinWaitlist({ event_id: eventId, tier_id: id, user_id: user.id, email: user.email || "" });
      toast({ title: t("ticketTier.toast.waitlistSuccessTitle"), description: t("ticketTier.toast.waitlistSuccessDesc") });
    } catch (err: any) {
      if (err?.message?.includes("duplicate") || err?.code === "23505") {
        toast({ title: t("ticketTier.toast.alreadyInWaitlist") });
      } else {
        toast({ title: t("ticketTier.toast.waitlistErrorTitle"), description: err?.message, variant: "destructive" });
      }
    } finally {
      setJoiningWaitlist(false);
    }
  };

  const isDisabled = isSoldOut || isNotYetOnSale || isSalesEnded;
  const serviceFee = calculateBuyerServiceFee(price, platformFeePercent, absorbFee);
  const totalPrice = calculateTicketTotal(price, platformFeePercent, absorbFee);

  return (
    <div className={cn(
      "flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 rounded-lg border border-border bg-card",
      isDisabled && "opacity-60", className
    )}>
      <div className="flex-1 space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          {sectorColor && (
            <span
              className="w-3.5 h-3.5 rounded-full shrink-0 border border-black/10 shadow-sm"
              style={{ backgroundColor: sectorColor }}
              title={t("ticketTier.sectorTooltip", { name })}
            />
          )}
          <h4 className="font-display font-semibold text-foreground">{name}</h4>
          {isLow && !isSoldOut && !isNotYetOnSale && (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-warning"><AlertCircle className="h-3 w-3" /> {t("ticketTier.lowQuantity")}</span>
          )}
          {isSoldOut && <span className="text-xs font-medium text-destructive">{t("ticketTier.soldOut")}</span>}
          {isNotYetOnSale && (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground"><Clock className="h-3 w-3" /> {t("ticketTier.salesStartIn", { time: formatCountdown(salesStart!) })}</span>
          )}
          {isSalesEnded && !isSoldOut && <span className="text-xs font-medium text-muted-foreground">{t("ticketTier.salesEnded")}</span>}
        </div>
        {description && <p className="text-sm text-muted-foreground">{description}</p>}
        <div className="flex items-baseline gap-2">
          {originalPrice && originalPrice > price && (
            <span className="text-sm text-muted-foreground line-through">{formatBRLOrFree(originalPrice)}</span>
          )}
          <span className="font-display font-bold text-foreground">{formatBRLOrFree(totalPrice)}</span>
          {tierType === "free" && <span className="text-xs text-muted-foreground">{t("ticketTier.freeLabel")}</span>}
        </div>
        {serviceFee > 0 && (
          <p className="text-xs text-muted-foreground">
            {formatBRLOrFree(price)} + {formatBRLOrFree(serviceFee)} de taxa de serviço
          </p>
        )}
        {includesCertificate && (
          <div className="flex items-center gap-1.5 text-xs text-primary font-medium mt-1">
            <Award className="h-3.5 w-3.5" />
            <span>{t("ticketTier.includesCertificate")}</span>
          </div>
        )}
        {isNotYetOnSale && salesStart && (
          <p className="text-xs text-muted-foreground">
            {t("ticketTier.salesStartAt", { date: formatEventDateTimeRange(salesStart, null, resolvedTimeZone, { dateStyle: "short", includeZoneLabel: false }) })}
          </p>
        )}
      </div>

      {!isDisabled && (
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 border border-border rounded-md">
            <button onClick={decrement} disabled={quantity <= 0} className="p-2 text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors">
              <Minus className="h-4 w-4" />
            </button>
            <span className="w-8 text-center text-sm font-medium tabular-nums">{quantity}</span>
            <button onClick={increment} disabled={quantity >= Math.min(maxPerOrder, available)} className="p-2 text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors">
              <Plus className="h-4 w-4" />
            </button>
          </div>
          <Button size="sm" onClick={handleAdd} disabled={isAdding || quantity === 0}>
            {isAdding ? t("ticketTier.adding") : t("ticketTier.add")}
          </Button>
        </div>
      )}

      {isSoldOut && eventId && (
        <Button variant="outline" size="sm" className="gap-1.5" onClick={handleJoinWaitlist} disabled={joiningWaitlist}>
          <Bell className="h-4 w-4" />
          {joiningWaitlist ? t("ticketTier.joining") : t("ticketTier.notifyMe")}
        </Button>
      )}
    </div>
  );
}
